import * as Encoding from 'encoding-japanese';
import { getApplicationText } from '../types/common/application-type';
import {
  DATE_FORMAT_SLASH,
  formatDate,
  formatUtcIsoToJstDateTime
} from './date-utils';
import {
  setAdditonalService,
  SumamoruAdditionalServices
} from '../enums/common/sumamoru-additional-services';
import { CsvType } from '../components/molecules/RealEstateSearchForm';
const moment = require('moment-timezone');

/**
 * CDEのCSVを作ってUploadしているところ参考
 */
export const CsvHeader = [
  '申請種別',
  '物件No.',
  '郵便番号',
  '住所',
  '番地',
  '集合住宅名・棟号数',
  '氏名（姓）',
  '氏名（名）',
  'フリガナ（姓）',
  'フリガナ（名）',
  '電話番号',
  'メールアドレス',
  '電気利用有無',
  'ガス利用有無',
  'お支払い方法',
  '電気申し込みプラン',
  '電気オプション割引',
  '電気の使用開始日',
  'ガスのお申し込みプラン',
  'ガスのオプション割引',
  'ガスの使用開始日',
  'ガスの使用開始日(時間帯)',
  '開栓の立ち合い者',
  '立ち合い者の氏名',
  '当日の連絡先',
  '退去日',
  '変更フラグ',
  'キャンセルフラグ',
  '紐付ID',
  '付帯サービスID1',
  '付帯サービスID2',
  '作成日時'
];

export function returnCsvValue(content: string | undefined | number): string {
  if (content === '' || content === undefined || content === null) {
    return '';
  } else {
    return `${content}`;
  }
}

export function CsvExportGenerator(records: any, csvType: CsvType): any {
  return records.map(record => {
    const sumamoruPlan = record.sumamoru_plan || '';
    const sumamoruAdditionalServices = record.sumamoruAdditionalServices || [];
    const [additionalService1 = '', additionalService2 = ''] =
      sumamoruAdditionalServices.length > 0
        ? record.sumamoruAdditionalServices
        : setAdditonalService(sumamoruPlan);
    return [
      returnCsvValue(getApplicationText(record.applicationCategory)),
      returnCsvValue(record.propertyServiceCode),
      returnCsvValue(record.postCode),
      returnCsvValue(record.prefecture + record.city),
      returnCsvValue(record.afterCity),
      returnCsvValue(record.address2),
      returnCsvValue(record.customerLastName),
      returnCsvValue(record.customerFirstName),
      returnCsvValue(record.customerLastNameKana),
      returnCsvValue(record.customerFirstNameKana),
      returnCsvValue(record.customerPhoneNumber),
      returnCsvValue(record.customerEmail),
      returnCsvValue(getElectricContractText[record.hasElectricContract]),
      returnCsvValue(getUseGasPlanSelectionText[record.useGasPlanSelection]),
      returnCsvValue(
        getPaymentMethodSelectionText[record.paymentMethodSelection]
      ),
      returnCsvValue(
        getElectricityBasePlanSelectionText[record.electricityBasePlanSelection]
      ),
      returnCsvValue(
        record.useGasPlanSelection === '0' ? 'ガスセット割引' : ''
      ),
      returnCsvValue(
        formatDate(record.electricityStartDate, DATE_FORMAT_SLASH)
      ),
      returnCsvValue(getGasBasePlanSelectionText[record.gasBasePlanSelection]),
      returnCsvValue(
        record.gasBasePlanSelection === '0' ? '電気セット割引' : ''
      ),
      returnCsvValue(formatDate(record.gasStartDate, DATE_FORMAT_SLASH)),
      returnCsvValue(
        getGasStartTimeSelectionText[record.gasStartTimeSelection]
      ),
      returnCsvValue(getWitnessGasStartText[record.witnessGasStart]),
      returnCsvValue(record.witnessNameGasStart),
      returnCsvValue(record.phoneNumberForGasStart),
      returnCsvValue(formatDate(record.serviceStopDate, DATE_FORMAT_SLASH)),
      returnCsvValue(record.isModifiedInSameDay === true ? '更新' : ''),
      returnCsvValue(record.isCanceledInSameDay === true ? 'キャンセル' : ''),
      returnCsvValue(record.linkageId),
      returnCsvValue(
        additionalService1 +
          (csvType === CsvType.NEW
            ? getAdditionalServiceSuffix(additionalService1, record)
            : '')
      ),
      returnCsvValue(
        additionalService2 +
          (csvType === CsvType.NEW
            ? getAdditionalServiceSuffix(additionalService2, record)
            : '')
      ),
      returnCsvValue(formatUtcIsoToJstDateTime(record.createdAt))
    ];
  });
}

export function generateFileName(
  targetService: string,
  csvType: string,
  startDate: string,
  endDate: string
): string {
  const start = moment(startDate).format('YYYYMMDD');
  const end = moment(endDate).format('YYYYMMDD');
  // 同一日付の場合は「YYYYMMDD」、異なる場合は「YYYYMMDD_YYYYMMDD」
  const dateSetting = moment(end).isSame(moment(start))
    ? `${end}`
    : `${start}_${end}`;
  // ファイル名の定義
  return `${targetService}_${csvType.toUpperCase()}_${dateSetting}.csv`;
}

export function convertToBlob(csvRows: string[][]): Blob {
  const csvContent = [
    CsvHeader.join(','),
    ...csvRows.map(row => row.join(','))
  ].join('\r\n');
  const unicodeList = [];
  for (let i = 0; i < csvContent.length; i += 1) {
    // @ts-ignore
    unicodeList.push(csvContent.charCodeAt(i));
  }
  // 変換処理の実施
  const shiftJisCodeList = Encoding.convert(unicodeList, 'sjis', 'unicode');
  const uInt8List = new Uint8Array(shiftJisCodeList);
  return new Blob([uInt8List], { type: 'text/csv' });
}

// ガス利用有無
export const getUseGasPlanSelectionText = {
  '0': '利用あり',
  '1': '利用なし'
};

// ガスのお申し込みプラン
export const getGasBasePlanSelectionText = {
  '0': 'ベーシックガス',
  '1': 'ゆかぽかガス'
};

// 電気利用有無
export const getElectricContractText = {
  true: '利用あり',
  false: '利用なし'
};

// お支払い方法
export const getPaymentMethodSelectionText = {
  '0': '口座振替を希望',
  '1': 'クレジットカード払いを希望'
};

// 電気申し込みプラン
export const getElectricityBasePlanSelectionText = {
  '0': 'スマモル賃貸プランB',
  '1': 'スマモル賃貸プランAE'
};

// ガスの使用開始日(時間帯)
export const getGasStartTimeSelectionText = {
  '0': 'ＡＭ',
  '1': 'ＰＭ（１３-１５時）',
  '2': 'ＰＭ（１５-１７時）',
  '3': 'ＥＶＥ（１７-１９時）',
  '4': 'ＰＭ（１３-１７時）'
};

// 開栓の立ち合い者
export const getWitnessGasStartText = {
  '0': '本人',
  '1': '家主',
  '2': '管理人',
  '3': 'その他'
};

export const getAdditionalServiceSuffix = (
  additionalService: SumamoruAdditionalServices,
  record: any
): string => {
  switch (additionalService) {
    case SumamoruAdditionalServices.BenefitOne:
      return record.boCustomerNo ? ` ${record.boCustomerNo}` : '';
    case SumamoruAdditionalServices.Viewn:
      return record.viewnContractId ? ` ${record.viewnContractId}` : '';
    default:
      return '';
  }
};
